import { motion, AnimatePresence } from "framer-motion";
import { OptionCard, JustificationPills, TextInput } from "../shared";
import { MarkdownContent } from "@/components/ui/markdown-content";
import type {
  ChooseJustifySpec,
  ChooseJustifyAnswer,
} from "@/types/hld-wizard";

const fadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.3 },
};

interface ChooseJustifyDecisionProps {
  spec: ChooseJustifySpec;
  context: string;
  question: string;
  answer: ChooseJustifyAnswer | undefined;
  onChange: (answer: ChooseJustifyAnswer) => void;
  disabled?: boolean;
}

export function ChooseJustifyDecision({
  spec,
  context,
  question,
  answer,
  onChange,
  disabled,
}: ChooseJustifyDecisionProps) {
  const selectedOptionId = answer?.option_id ?? "";

  // Resolve adaptive sets
  const justificationItems = spec.justifications.adaptive
    ? spec.justifications.sets[selectedOptionId] ?? []
    : spec.justifications.sets["_default"] ?? [];

  const downsidePrompt = spec.downside.adaptive
    ? spec.downside.prompts[selectedOptionId] ?? ""
    : spec.downside.prompts["_default"] ?? "";

  function selectOption(optionId: string) {
    onChange({
      option_id: optionId,
      justification_ids: [],
      downside_text: "",
    });
  }

  function toggleJustification(id: string) {
    if (!answer) return;
    const current = answer.justification_ids;
    const next = current.includes(id)
      ? current.filter((j) => j !== id)
      : current.length < spec.justifications.max_selections
        ? [...current, id]
        : current;
    onChange({ ...answer, justification_ids: next });
  }

  function setDownsideText(text: string) {
    if (!answer) return;
    onChange({ ...answer, downside_text: text });
  }

  return (
    <div className="space-y-6">
      {context && (
        <div className="border-l-2 border-primary/30 pl-4 text-muted-foreground">
          <MarkdownContent>{context}</MarkdownContent>
        </div>
      )}

      <MarkdownContent className="font-medium">{question}</MarkdownContent>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {spec.options.map((opt) => (
          <OptionCard
            key={opt.id}
            option={opt}
            selected={selectedOptionId === opt.id}
            onClick={() => selectOption(opt.id)}
            disabled={disabled}
          />
        ))}
      </div>

      <AnimatePresence>
        {selectedOptionId && justificationItems.length > 0 && (
          <motion.div {...fadeIn} className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">
              Select up to {spec.justifications.max_selections} justifications:
            </p>
            <JustificationPills
              items={justificationItems}
              selected={answer?.justification_ids ?? []}
              onToggle={toggleJustification}
              max={spec.justifications.max_selections}
              disabled={disabled}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {answer && answer.justification_ids.length > 0 && downsidePrompt && (
          <motion.div {...fadeIn}>
            <TextInput
              value={answer.downside_text}
              onChange={setDownsideText}
              placeholder={downsidePrompt}
              maxChars={spec.downside.max_chars}
              disabled={disabled}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
